// The HTTP client that replaces the `pocketbase` SDK's transport (`pb.send`).
//
// Everything the app needed from the SDK is here: an authenticated fetch, a
// typed error with the SDK's semantics, and attachment URL building. What is
// deliberately NOT here: a collection API, a filter DSL, or record CRUD — every
// read goes through a purpose-built /app endpoint (ADR-0007) and every write
// through /api/commands/*, so there is nothing generic left to wrap.
import { Config } from "@/config";
import { authStore } from "./auth-store";

const baseUrl = (await Config.pocketbaseUrl()).replace(/\/+$/, "");

/**
 * A failed request. `status` is the HTTP status, or **0** when the request
 * never got a server verdict at all (offline, DNS failure, abort, timeout).
 *
 * That 0 is a load-bearing contract, not an accident: write-queue.ts decides
 * whether to retry a queued mutation by asking "was this a transport failure or
 * a server verdict?", and a server verdict must never be retried. `response` is
 * the parsed error body (the SDK's `ClientResponseError.response`), which
 * commands.ts unwraps for validation details.
 */
export class ApiError extends Error {
  readonly status: number;
  /** The parsed JSON error body, when the server sent one. */
  readonly response: unknown;
  /** True when the request was cancelled via its AbortSignal. */
  readonly isAbort: boolean;

  constructor(status: number, message: string, response?: unknown, opts?: { isAbort?: boolean }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
    this.isAbort = opts?.isAbort ?? false;
  }
}

/**
 * Returns true when an error is a 401 from the server (invalid/expired token).
 * Use this in catch blocks to silently skip user-facing error messages — api()
 * already cleared the auth store and AuthProvider will redirect to /login.
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

// Loose on purpose, like the SDK's SendOptions.query: callers pass whatever
// scalar they have and serialization stringifies it; null/undefined/"" are
// dropped, arrays repeat the key.
export type Query = Record<string, unknown>;

export type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** JSON body. Ignored when `formData` is set. */
  body?: unknown;
  /** Multipart body, for file uploads. Sent as-is so fetch sets the boundary. */
  formData?: FormData;
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Hands the request to the browser to finish after the page goes away. Only
   * for writes started from a page-hide handler — a closing page cancels an
   * ordinary request and the retry queue never gets to run.
   */
  keepalive?: boolean;
};

/**
 * Serializes query params. A repeated key is emitted once per value
 * (`vessel_ids=A&vessel_ids=B`), which is the shape every list endpoint reads;
 * empty and nullish values are dropped so an unset filter is absent rather
 * than present-and-empty.
 */
export function buildQuery(query: Query | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === "") continue;
        params.append(key, String(item));
      }
      continue;
    }
    params.append(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

/** Absolute URL for an API path, for cases that cannot go through `api()` (EventSource, <img src>). */
export function apiUrl(path: string, query?: Query): string {
  return `${baseUrl}${path}${buildQuery(query)}`;
}

/**
 * Authenticated fetch. Returns the parsed JSON body, or `undefined` for a 204.
 * Throws {@link ApiError} for every non-2xx response and for transport
 * failures (status 0), exactly like `pb.send` threw `ClientResponseError`.
 */
// The default is `any`, matching the SDK's `send<T = any>`: untyped call sites
// relied on it and typing them all is churn for another day.
// biome-ignore lint/suspicious/noExplicitAny: parity with pb.send
export async function api<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { ...options.headers };

  let body: BodyInit | undefined;
  if (options.formData) {
    // Never set Content-Type for FormData: the boundary is generated by fetch.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  // The raw token, no "Bearer" prefix — byte-identical to what the SDK sent.
  // (PocketBase strips an optional Bearer prefix, but the commands API's own
  // middleware is not obliged to, so don't introduce one.)
  const token = authStore.token;
  if (token) headers.Authorization = token;

  let response: Response;
  try {
    response = await fetch(apiUrl(path, options.query), {
      method,
      headers,
      body,
      signal: options.signal,
      keepalive: options.keepalive,
    });
  } catch (error) {
    // fetch rejects only on transport failure and abort. Both are status 0:
    // nothing was decided by the server, so a queued write may be retried.
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    throw new ApiError(0, error instanceof Error ? error.message : "Network request failed", undefined, { isAbort });
  }

  if (response.status === 401) {
    // The session is gone; drop it so AuthProvider redirects to /login. 403 is
    // deliberately excluded — it means "authenticated but not allowed here",
    // and signing the user out at every permission boundary would be wrong.
    authStore.clear();
  }

  if (!response.ok) {
    const errorBody = await safeParse(response);
    throw new ApiError(response.status, messageFrom(errorBody, response), errorBody);
  }

  if (response.status === 204) return undefined as T;
  return (await safeParse(response)) as T;
}

/**
 * Builds an authenticated download URL for a mission attachment.
 *
 * The attachment file field is Protected server-side (pentest FRY-7, migration
 * 355), so PocketBase requires a short-lived file token and enforces the
 * collection's view rule before serving the bytes. Always route attachment URLs
 * through here — a plain files URL without the token now 404s.
 */
export async function getAttachmentURL(record: {
  id: string;
  file: string;
  collectionId?: string;
  collectionName?: string;
}) {
  const { token } = await api<{ token: string }>("/api/files/token", { method: "POST" });
  const collection = record.collectionId || record.collectionName || "";
  return apiUrl(
    `/api/files/${encodeURIComponent(collection)}/${encodeURIComponent(record.id)}/${encodeURIComponent(record.file)}`,
    { token },
  );
}

/** Health ping (GET /api/health). Resolves when the backend answers 2xx, throws otherwise. */
export async function pingHealth(signal?: AbortSignal): Promise<void> {
  await api("/api/health", { signal });
}

async function safeParse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// PocketBase's error bodies are `{"message": "..."}` and the commands API's are
// `{"error": "..."}`; anything else falls back to the status line so a message
// is always present for the UI.
function messageFrom(body: unknown, response: Response): string {
  if (body && typeof body === "object") {
    for (const key of ["message", "error"] as const) {
      const value = (body as Record<string, unknown>)[key];
      if (typeof value === "string" && value !== "") return value;
    }
  }
  if (typeof body === "string" && body !== "") return body;
  return `${response.status} ${response.statusText}`.trim();
}
